import { mapHexCodeToEmoticon } from '@assembly-web/services';
import {
  Content,
  Item,
  Menu,
  Portal,
  Root,
  Separator,
  Sub,
  Trigger,
} from '@radix-ui/react-menubar';
import type { ReactNode } from 'react';
import { useEffect, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import { TextStyle } from '../../../DesignSystem/Feedback/TextStyle';
import { classNames } from '../../../DesignSystem/Utils/classNames';

type Flow = {
  id: string;
  name: string;
  icon: { kind: string; value: string };
};

type FlowsToDoMenuProps = {
  alignOffset?: number;
  answerNowFlows: Flow[];
  children: ReactNode;
  answerAnytimeFlows: Flow[];
  onMenuItemClick: (flowId: string, flowName: string) => void;
  defaultOpen?: boolean;
  handleOnClose?: () => void;
};

const messages = defineMessages({
  answerNow: {
    defaultMessage: 'Answer now',
    id: 'Bpz0xZ',
  },
  answerAnytime: {
    defaultMessage: 'Answer anytime',
    id: '4euen7',
  },
});

export function FlowsToDoMenu(props: FlowsToDoMenuProps) {
  const {
    alignOffset = 0,
    answerAnytimeFlows,
    answerNowFlows,
    onMenuItemClick,
    children,
    defaultOpen = false,
    handleOnClose,
  } = props;

  const [open, setOpen] = useState(false);

  const { formatMessage } = useIntl();

  useEffect(() => {
    if (defaultOpen && !open) setOpen(true);
  }, [defaultOpen, open]);

  const MenuItems = () => {
    return (
      <>
        <Trigger
          onClick={() => {
            setOpen(!open);
          }}
          className="relative inline-block text-left"
          asChild
        >
          {children}
        </Trigger>
        <Portal>
          <Content
            className="absolute left-0 z-[4] max-h-[32rem] w-56 origin-top transform overflow-auto rounded-md border border-gray-5 bg-gray-1 shadow-lg-down ring-opacity-5 focus:outline-none"
            align="start"
            sideOffset={5}
            alignOffset={alignOffset}
            avoidCollisions={false}
            hideWhenDetached
          >
            {answerNowFlows.length > 0 && (
              <TextStyle variant="sm-regular" className="px-3 py-2 text-gray-7">
                {formatMessage(messages.answerNow)}
              </TextStyle>
            )}

            {answerNowFlows.map((flow) => (
              <Item
                key={flow.id}
                className="focus:bg-primary-2 focus:outline-none"
                onClick={() => onMenuItemClick(flow.id, flow.name)}
              >
                <button
                  className={classNames(
                    'flex w-full items-center rounded-md px-3 py-2 text-sm text-gray-8 hover:bg-primary-2'
                  )}
                >
                  <div className="flex">
                    <div className="mr-2">
                      {mapHexCodeToEmoticon(flow.icon.value)}
                    </div>
                    <div className="line-clamp-1 text-left">{flow.name}</div>
                  </div>
                </button>
              </Item>
            ))}

            <Separator className="MenubarSeparator" />
            <TextStyle variant="sm-regular" className="px-3 py-2 text-gray-7">
              {formatMessage(messages.answerAnytime)}
            </TextStyle>
            {answerAnytimeFlows.map((flow) => (
              <Item
                key={flow.id}
                className="focus:bg-primary-2 focus:outline-none"
                onClick={() => onMenuItemClick(flow.id, flow.name)}
              >
                <button
                  className={classNames(
                    'flex w-full items-center rounded-md px-3 py-2 text-sm text-gray-8 hover:bg-primary-2'
                  )}
                >
                  <div className="flex">
                    <div className="mr-2">
                      {mapHexCodeToEmoticon(flow.icon.value)}
                    </div>
                    <div className="line-clamp-1 text-left">{flow.name}</div>
                  </div>
                </button>
              </Item>
            ))}
          </Content>
        </Portal>
      </>
    );
  };

  return (
    <Root>
      <Menu>
        <Sub
          open={open}
          onOpenChange={(isOpen) => {
            setOpen(isOpen);
            if (!isOpen && handleOnClose) {
              handleOnClose();
            }
          }}
        >
          <MenuItems />
        </Sub>
      </Menu>
    </Root>
  );
}
